<template>
  <div>
    <v-row dense>
      <v-col cols="12">
        <div class="d-flex align-center text-orange-accent-4 mt-3">
          <v-icon icon="mdi-alert" color="orange-accent-4" class="mr-2" />
          <span> Notice: Be sure to save after updating values for it to be reflected. </span>
        </div>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="8">
        <v-text-field
          v-model="tenantSearch"
          label="Search"
          append-icon="mdi-magnify"
          variant="underlined"
          color="primary"
        />
      </v-col>
      <v-col cols="4" align="end">
        <cs-button
          primary
          label="Add Tenant"
          class="mt-4"
          left-icon="mdi-plus-circle"
          :disabled="isFormReadOnly"
          @click="onAddClick"
        />
      </v-col>
    </v-row>
    <v-data-table
      :items="inputFields"
      density="compact"
      color="primary"
      height="400"
      fixed-header
      disable-sort
      :search="tenantSearch"
      :loading="fetchHubCsvTenantsInProgress"
      :headers="headers"
    >
      <template #[`item.tenantName`]="{ item }">
        {{ item.tenantName || '-' }}
      </template>
      <template #[`item.tenantSubDomain`]="{ item }">
        {{ item.tenantSubDomain || '-' }}
      </template>
      <template #[`item.serviceTier`]="{ item }">
        {{ item.serviceTier || '-' }}
      </template>
      <template #[`item.actions`]="{ index }">
        <v-icon
          :disabled="isFormReadOnly"
          icon="mdi-pencil"
          class="mr-2"
          color="primary"
          aria-label="Edit row"
          @click="onEditClick(index)"
        />
        <v-icon
          :disabled="isFormReadOnly"
          icon="mdi-delete"
          color="primary"
          aria-label="Delete row"
          @click="onDeleteClick(index)"
        />
      </template>
    </v-data-table>
    <v-row dense class="mt-4">
      <v-col cols="12" class="d-flex justify-end">
        <cs-button
          primary
          label="Save"
          :loading="saveInProgress"
          :disabled="isFormReadOnly"
          @click="onSaveClick()"
        />
      </v-col>
    </v-row>

    <!-- Form Dialog (Add/Edit Tenant) -->
    <cs-form-dialog
      id="dlg-client-hub-tenant-management-form"
      v-model="formDialogDisplayed"
      body-id="dlg-client-hub-tenant-management-form-body"
      :heading="editingIndex !== null ? 'Edit Tenant' : 'Add Tenant'"
      :primary-action="{
        label: editingIndex !== null ? 'Update' : 'Add',
        eventName: 'primary-click'
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click'
      }"
      @primary-click="onFormSave()"
      @secondary1-click="onFormCancel()"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="formFields.tenantName"
                label="Tenant Name"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.tenantName"
                @input="formErrors.tenantName = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="formFields.tenantSubDomain"
                label="Tenant SubDomain"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.tenantSubDomain"
                @input="formErrors.tenantSubDomain = ''"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="formFields.serviceTier"
                label="Service Tier"
                variant="underlined"
                color="primary"
                :error-messages="formErrors.serviceTier"
                :items="serviceTierOptions"
              />
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <!-- Success Dialog -->
    <cs-form-dialog
      id="dlg-client-hub-tenant-management-save-success"
      v-model="showSaveSuccessDialog"
      heading="Save Success"
      body-id="dlg-client-hub-tenant-management-save-success-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showSaveSuccessDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <span> The tenant information has been saved successfully. </span>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <!-- Error Dialog -->
    <cs-form-dialog
      id="dlg-client-hub-tenant-management-save-error"
      v-model="showSaveErrorDialog"
      heading="Save Error"
      body-id="dlg-client-hub-tenant-management-save-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showSaveErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>There is some error while saving the tenant information. Please try again.</p>
                <p class="text-red-accent-4">{{ errorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-tenant-management-validation-error"
      v-model="showValidationErrorDialog"
      heading="Validation Error"
      body-id="dlg-client-hub-tenant-management-validation-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="showValidationErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p class="text-red-accent-4">{{ validationErrorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>

    <cs-form-dialog
      id="dlg-client-hub-tenant-management-fetch-error"
      v-model="fetchHubCsvTenantsErrorDialog"
      heading="Fetch Tenant Error"
      body-id="dlg-client-hub-tenant-management-fetch-error-body"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="fetchHubCsvTenantsErrorDialog = false"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-0">
          <v-row no-gutters>
            <v-col cols="12">
              <div>
                <p>There is some error while fetching the tenant information. Please try again.</p>
                <p class="text-red-accent-4">{{ fetchHubCsvTenantsErrorMessage }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import * as types from '@/store/mutationTypes';
import { validationRules } from '@/helpers/validationRules';

const FIELD_MAP = {
  tenantName: 'TenantName',
  tenantSubDomain: 'TenantSubDomain',
  serviceTier: 'ServiceTier'
};

const mapTenantFields = (source, isFormatToApi = false) => {
  const entries = Object.entries(FIELD_MAP);
  if (isFormatToApi) {
    return Object.fromEntries(entries.map(([ui, api]) => [api, source[ui]]));
  }
  const base = Object.fromEntries(entries.map(([ui, api]) => [ui, source[api]]));
  base.serviceTier = base.serviceTier || 'LITE';
  return base;
};

const DEFAULT_INPUT_FIELDS = {
  tenantName: '',
  tenantSubDomain: '',
  serviceTier: 'LITE'
};

const DEFAULT_FORM_ERRORS = {
  tenantName: '',
  tenantSubDomain: '',
  serviceTier: ''
};

export default {
  name: 'ClientHubTenantManagement',

  props: {
    clientSid: { type: String, required: false, default: '' },
    uploadFileInProgress: { type: Boolean, required: false, default: false }
  },

  emits: ['save-success'],

  data() {
    return {
      tenantSearch: '',
      headers: [
        { title: 'Tenant Name', key: 'tenantName' },
        { title: 'Tenant Sub Domain', key: 'tenantSubDomain' },
        { title: 'Service Tier', key: 'serviceTier' },
        { title: 'Actions', key: 'actions', align: 'center' }
      ],
      inputFields: [],
      formErrors: cloneDeep(DEFAULT_FORM_ERRORS),
      formFields: cloneDeep(DEFAULT_INPUT_FIELDS),
      serviceTierOptions: ['LITE', 'ESSENTIALS'],
      saveInProgress: false,
      formDialogDisplayed: false,
      editingIndex: null,
      fetchHubCsvTenantsInProgress: false,
      fetchHubCsvTenantsErrorDialog: false,
      fetchHubCsvTenantsErrorMessage: '',
      showSaveSuccessDialog: false,
      showSaveErrorDialog: false,
      errorMessage: '',
      showValidationErrorDialog: false,
      validationErrorMessage: ''
    };
  },

  computed: {
    isFormReadOnly() {
      return this.saveInProgress || this.uploadFileInProgress || this.fetchHubCsvTenantsInProgress;
    }
  },

  async mounted() {
    await this.fetchHubCsvTenants();
  },

  methods: {
    onDeleteClick(index) {
      this.inputFields.splice(index, 1);
    },

    onAddClick() {
      this.editingIndex = null;
      this.formFields = cloneDeep(DEFAULT_INPUT_FIELDS);
      this.formErrors = cloneDeep(DEFAULT_FORM_ERRORS);
      this.formDialogDisplayed = true;
    },

    onEditClick(index) {
      this.editingIndex = index;
      this.formFields = cloneDeep(this.inputFields[index]);
      this.formErrors = cloneDeep(DEFAULT_FORM_ERRORS);
      this.formDialogDisplayed = true;
    },

    onFormSave() {
      const isValid = this.validateForm();
      if (!isValid) return;

      if (this.editingIndex !== null) {
        this.inputFields[this.editingIndex] = cloneDeep(this.formFields);
      } else {
        this.inputFields.push(cloneDeep(this.formFields));
      }
      this.formDialogDisplayed = false;
    },

    onFormCancel() {
      this.formFields = cloneDeep(DEFAULT_INPUT_FIELDS);
      this.formErrors = cloneDeep(DEFAULT_FORM_ERRORS);
      this.formDialogDisplayed = false;
    },

    validateTenantName(v) {
      const req = validationRules.required('Tenant name')(v);
      if (req !== true) return req;
      const min = validationRules.minLength('Tenant name', 2)(v);
      if (min !== true) return min;
      const max = validationRules.maxLength('Tenant name', 100)(v);
      if (max !== true) return max;
      return true;
    },

    validateTenantSubDomain(v) {
      const req = validationRules.required('Tenant subdomain')(v);
      if (req !== true) return req;
      const min = validationRules.minLength('Tenant subdomain', 2)(v);
      if (min !== true) return min;
      const max = validationRules.maxLength('Tenant subdomain', 31)(v);
      if (max !== true) return max;
      const duplicate = this.inputFields.some(
        (item, i) =>
          i !== this.editingIndex && item.tenantSubDomain.toLowerCase() === v.toLowerCase()
      );
      if (duplicate) return 'Tenant subdomain already exists in the list';
      return validationRules.pattern(
        'Tenant subdomain',
        /^[a-z0-9](?:[a-z0-9-]{0,29}[a-z0-9])?$/,
        'Lowercase letters, numbers, and hyphens only. Must start and end with a letter or number.'
      )(v);
    },

    validateServiceTier(v) {
      return validationRules.oneOf('Service tier', ['LITE', 'ESSENTIALS'])(v);
    },

    validateForm() {
      const errors = {
        tenantName: this.validateTenantName(this.formFields.tenantName),
        tenantSubDomain: this.validateTenantSubDomain(this.formFields.tenantSubDomain),
        serviceTier: this.validateServiceTier(this.formFields.serviceTier)
      };

      this.formErrors = Object.fromEntries(
        Object.entries(errors).map(([key, val]) => [key, val === true ? '' : val])
      );

      return Object.values(errors).every((v) => v === true);
    },

    async onSaveClick() {
      if (this.inputFields.length === 0) {
        this.showValidationErrorDialog = true;
        this.validationErrorMessage = 'At least one Tenant record(s) is required to save.';
        return;
      }

      try {
        if (this.saveInProgress) return;
        this.saveInProgress = true;

        const tenantInfo = this.inputFields.map((item) => mapTenantFields(item, true));

        await this.$store.dispatch('hub-migration/postCsvTenants', {
          clientSid: this.clientSid,
          tenantInfo
        });
        this.showSaveSuccessDialog = true;
        this.$emit('save-success');
        await this.fetchHubCsvTenants();
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || error.message;
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.showSaveErrorDialog = true;
      } finally {
        this.saveInProgress = false;
      }
    },

    async fetchHubCsvTenants() {
      if (this.fetchHubCsvTenantsInProgress) return;
      this.fetchHubCsvTenantsInProgress = true;

      try {
        const tenants = await this.$store.dispatch('hub-migration/getCsvTenants', {
          clientSid: this.clientSid
        });

        this.inputFields = (tenants?.tenantInfo || []).map((tenant) => mapTenantFields(tenant));
      } catch (err) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.fetchHubCsvTenantsErrorMessage = err?.response?.data?.message || err.message;
        this.fetchHubCsvTenantsErrorDialog = true;
      } finally {
        this.fetchHubCsvTenantsInProgress = false;
      }
    }
  }
};
</script>
